"use client";

import { useApplicationPlugin } from "@shesha-io/reactjs";
import { usePathname } from "next/navigation";
import { useEffect, useMemo } from "react";

// Written by the auth-login post-login script, re-stamped by the dashboard's
// onBeforeDataLoad guard and the registration wizards' submit scripts;
// "0" means a member has not completed tier selection yet.
const SETUP_FLAG_KEY = "pbfSetupComplete";

const TIER_PRICING_URL = "/dynamic/PBF.MembershipManagement/tier-pricing";

// The only routes an onboarding member may visit. The dashboard is allowed because its
// own onBeforeDataLoad guard re-checks against live data (and corrects a stale flag).
const ALLOWED_WHILE_INCOMPLETE: RegExp[] = [
  /^\/login/i,
  /^\/no-auth\//i,
  /\/tier-pricing$/i,
  /\/tier-more-info$/i,
  /\/member-registration(-pcf)?$/i,
  /\/dashboard$/i,
];

function readSetupComplete(): boolean {
  if (typeof window === "undefined") return true;
  // Default to complete when the flag is absent — admins and host users never carry it;
  // members get it stamped at login before any portal page renders.
  return window.localStorage.getItem(SETUP_FLAG_KEY) !== "0";
}

/**
 * Locks portal navigation until a member's account setup (tier selection) is complete.
 *
 * 1. Route guard: on every route (full loads AND client-side transitions) an incomplete
 *    member is bounced to tier selection — side-menu links and deep links included.
 * 2. Exposes `application.setup.complete` for Shesha code editors (same pattern as
 *    `application.brand.*`) — the sidebar menu's item-visibility scripts read it;
 *    localStorage is not reachable from the jsSetting evaluator, so this bridges it.
 */
export const SetupPlugin = () => {
  const pathname = usePathname();
  const setup = useMemo(() => ({ complete: readSetupComplete() }), []);

  useEffect(() => {
    if (!pathname) return;
    if (readSetupComplete()) return;
    if (ALLOWED_WHILE_INCOMPLETE.some((rx) => rx.test(pathname))) return;
    // Full reload (not SPA push) so the sidebar menu re-evaluates its visibility scripts.
    window.location.href = TIER_PRICING_URL;
  }, [pathname]);

  useApplicationPlugin({
    name: "setup",
    data: setup,
    buildMetadata: (appBuilder) => {
      appBuilder.addObject("setup", "Setup", (setupBuilder) => {
        setupBuilder.addBoolean("complete", "Account setup workflow complete");
      });
    },
  });

  return null;
};
